/**
 * The Games page's body (place manager, unit 8): the list with its ticks, the
 * change applied here with its Undo, and the step's actions. Draws only from
 * its props and reads nothing (no data module, no transport), so every state
 * renders in tests and the headless proof; `games-page.tsx` feeds it from the
 * store.
 */
import * as React from "react";
import { Gamepad2 } from "lucide-react";

import { PillBadge, SampleChip } from "@/features/places/place-today";
import { undoOffered } from "@/features/writes/writes";
import { cn, plural } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  changeOf,
  changedLine,
  choicesOf,
  headerLine,
  needsSecondTap,
  reviewLine,
  unknownRowLine,
  type BrandGamesRead,
  type GameChoice,
  type GamesChanged,
} from "@/features/games/brand-games";
import type { GamesStep } from "@/features/games/games-store";

export interface GamesViewProps {
  read: BrandGamesRead;
  sample: boolean;
  rereading: boolean;
  /** The latest read again failed: the list shown is the last good read. */
  refreshFailed: boolean;
  ticked: ReadonlySet<string>;
  step: GamesStep;
  changed: GamesChanged | null;
  now: number;
  onTick?: (slug: string, on: boolean) => void;
  onReview?: () => void;
  onDiscard?: () => void;
  onBack?: () => void;
  onConfirmEmpty?: () => void;
  onApply?: () => void;
  onClose?: () => void;
  onUndo?: () => void;
}

/** The list with its ticks, the change applied here (with "Undo"), and the
 *  step's actions. Draws only from its props, so every state renders in tests. */
export function GamesView(props: GamesViewProps) {
  const { read, sample, rereading, refreshFailed, ticked, step, changed, now } = props;
  // Worked out once per read, ticks and step: "Undo" re-renders every second.
  const model = React.useMemo(() => {
    const change = step.kind === "edit" ? changeOf(read, ticked) : step.change;
    return {
      choices: choicesOf(read),
      header: headerLine(read),
      unknownLine: unknownRowLine(read.catalog),
      change,
      review: step.kind === "edit" || !change ? null : { line: reviewLine(change, read.catalog), secondTap: needsSecondTap(change, read.catalog) },
    };
  }, [read, ticked, step]);
  const { choices, change, unknownLine } = model;
  const locked = step.kind !== "edit" || changed?.undo === "undoing";
  return (
    <Card>
      <CardHeader data-section="Games">
        <CardTitle>
          <Gamepad2 /> <span data-games-header>{model.header}</span>
        </CardTitle>
        <span className="ml-auto flex items-center gap-2 text-xs text-text-3">
          {rereading && "Reading again…"}
          {!rereading && refreshFailed && "The latest read failed; this is the last good read."}
          {sample && <SampleChip />}
        </span>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pt-3">
        {changed && <ChangedNote changed={changed} now={now} held={step.kind !== "edit"} onUndo={props.onUndo} />}
        <ul className="flex flex-col divide-y divide-border">
          <li aria-hidden className="flex items-center gap-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-[.06em] text-text-3">
            <span className="w-[18px] shrink-0" />
            <span className="flex-1">Runs here</span>
          </li>
          {choices.map((c) => (
            <ChoiceRow
              key={c.slug}
              choice={c}
              ticked={ticked.has(c.slug)}
              locked={locked}
              unknownLine={unknownLine}
              onTick={props.onTick}
            />
          ))}
        </ul>
        {step.kind === "edit" ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={!change || locked} onClick={props.onReview}>
              Review
            </Button>
            {change && (
              <Button size="sm" variant="ghost" onClick={props.onDiscard}>
                Discard
              </Button>
            )}
            <span className="text-xs text-text-3">
              {change
                ? `${change.adds.length + change.removes.length} ${plural(change.adds.length + change.removes.length, "change")}, not applied yet.`
                : "Tick the games this brand's places run, then Review."}
            </span>
            {step.notice && <p className="basis-full text-[13px] font-semibold text-failure">{step.notice}</p>}
          </div>
        ) : (
          <Review {...props} step={step} line={model.review?.line ?? ""} secondTap={model.review?.secondTap ?? false} />
        )}
      </CardContent>
    </Card>
  );
}

/** One game: its tick, name, slug and engine; switched off, greyed and
 *  marked; a listed slug the catalog does not have, said as such. */
function ChoiceRow({
  choice,
  ticked,
  locked,
  unknownLine,
  onTick,
}: {
  choice: GameChoice;
  ticked: boolean;
  locked: boolean;
  unknownLine: string;
  onTick?: (slug: string, on: boolean) => void;
}) {
  const { game, slug } = choice;
  const id = `game-${slug}`;
  return (
    <li data-game={slug} className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 py-2", game && !game.enabled && "opacity-60")}>
      <Checkbox
        id={id}
        checked={ticked}
        disabled={locked}
        aria-label={`${game?.name ?? slug} runs here`}
        onCheckedChange={(on) => onTick?.(slug, on === true)}
      />
      <label htmlFor={id} className={cn("flex min-w-0 flex-1 items-center gap-1.5", !locked && "cursor-pointer")}>
        {game ? (
          <>
            <span className="truncate text-sm font-semibold">{game.name}</span>
            {!game.enabled && <PillBadge>disabled</PillBadge>}
          </>
        ) : (
          <span className="truncate font-mono text-sm text-text-3">{slug}</span>
        )}
      </label>
      {game ? (
        <>
          <span className="font-mono text-xs text-text-3">{game.slug}</span>
          <span className="w-20 text-right text-xs text-muted-foreground">{game.engine}</span>
        </>
      ) : (
        <span className="text-xs text-text-3">{unknownLine}</span>
      )}
    </li>
  );
}

/** The review of one change: its one line (with the second tap when the list
 *  sent is empty), then Apply and Back; after a lost answer, its line and Close. */
function Review({
  step,
  line,
  secondTap,
  onConfirmEmpty,
  onApply,
  onBack,
  onClose,
}: GamesViewProps & { step: Exclude<GamesStep, { kind: "edit" }>; line: string; secondTap: boolean }) {
  // An empty list: the app shows no games. Otherwise none of the catalog's.
  const none = step.change.after.length === 0;
  const confirmed = step.kind !== "lost" && step.confirmed;
  return (
    <div className="flex flex-col gap-2.5">
      <div data-review className="flex flex-wrap items-center gap-2 rounded-[10px] border border-border bg-panel-2 px-3 py-2.5 text-[13px] font-semibold">
        <span>{line}</span>
        {secondTap && step.kind === "review" && !confirmed && (
          <Button data-confirm-empty size="sm" variant="subtle" onClick={onConfirmEmpty}>
            {none ? "Yes, show no games" : "Yes, apply it"}
          </Button>
        )}
        {secondTap && confirmed && (
          <span className="text-xs font-semibold text-text-3">
            {none ? "Confirmed: the app will show no games." : "Confirmed: no game from the catalog will show."}
          </span>
        )}
      </div>
      {step.kind === "lost" ? (
        <>
          <p className="text-[13px] font-semibold text-failure">{step.line}</p>
          <div>
            <Button size="sm" variant="subtle" onClick={onClose}>
              Close
            </Button>
          </div>
        </>
      ) : (
        <>
          {step.failed && <p className="text-[13px] font-semibold text-failure">{step.failed}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={step.kind === "applying" || (secondTap && !confirmed)} onClick={onApply}>
              {step.kind === "applying" ? "Applying…" : "Apply"}
            </Button>
            <Button size="sm" variant="subtle" disabled={step.kind === "applying"} onClick={onBack}>
              Back
            </Button>
            <span className="text-xs text-text-3">
              {secondTap && !confirmed ? `Tap “${none ? "Yes, show no games" : "Yes, apply it"}” first. ` : ""}The whole list is sent; Undo puts it back for five minutes.
            </span>
          </div>
        </>
      )}
    </div>
  );
}

/** What the change applied here says, with "Undo" while it lasts; held (not
 *  shown) while another change is reviewed. */
function ChangedNote({ changed, now, held, onUndo }: { changed: GamesChanged; now: number; held: boolean; onUndo?: () => void }) {
  const failed = typeof changed.undo === "object" && "failed" in changed.undo ? changed.undo.failed : null;
  const offered = undoOffered(changed, now);
  const undoShown = offered && !held;
  return (
    <div data-changed-line className="flex flex-wrap items-center gap-2 rounded-[10px] border border-border bg-panel-2 px-3 py-2.5 text-xs font-semibold text-text-3">
      <span>{changedLine(changed)}</span>
      {changed.undo === "undoing" && <span>Undoing…</span>}
      {undoShown && (
        <Button size="sm" variant="subtle" onClick={onUndo}>
          Undo
        </Button>
      )}
      {offered && held && <span>Undo waits until the review is closed.</span>}
      {failed && <span className="text-failure">{offered ? failed : "Undo did not go through, and its five minutes have passed: tick the games again to put them back."}</span>}
    </div>
  );
}
